import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useApi, apiResolver } from "@/api/useApi";
import {
  ReleaseConversationFilters,
  ReleaseConversationResponse,
  SearchReleaseConversationsDto,
  SearchGoodFeedbackReleaseConversationsResponse,
  UpdateReleaseConversationFeedbackDto,
  BatchUpdateReleaseConversationStatusDto,
  BatchUpdateFeedbackResponse,
  ReleaseConversationFeedback,
  ReleaseComment,
} from "./ReleaseConversation.model";

export function useReleaseConversationApi() {
  const { api } = useApi();

  return useMemo(
    () => ({
      getReleaseConversations: async (
        filters: ReleaseConversationFilters = {}
      ): Promise<ReleaseConversationResponse> => {
        const params = new URLSearchParams();

        if (filters.page) params.append("page", filters.page.toString());
        if (filters.limit) params.append("limit", filters.limit.toString());
        if (filters.topicIds?.length) {
          filters.topicIds.forEach((id) =>
            params.append("topicIds[]", id.toString())
          );
        }
        if (filters.releaseIds?.length) {
          filters.releaseIds.forEach((id) =>
            params.append("releaseIds[]", id.toString())
          );
        }
        if (filters.commentFilter) {
          params.append("commentFilter", filters.commentFilter);

          const trimmedCommentFilter = filters.commentFilter.trim();
          if (
            trimmedCommentFilter !== "" &&
            !Number.isNaN(Number(trimmedCommentFilter))
          ) {
            params.append("ids[]", trimmedCommentFilter);
          }
        }
        if (filters.hasRelatedConversation !== undefined) {
          params.append(
            "hasRelatedConversation",
            filters.hasRelatedConversation.toString()
          );
        }
        if (filters.hasRelatedTicket !== undefined) {
          params.append(
            "hasRelatedTicket",
            filters.hasRelatedTicket.toString()
          );
        }
        if (filters.hasRelatedTraining !== undefined) {
          params.append(
            "hasRelatedTraining",
            filters.hasRelatedTraining.toString()
          );
        }
        if (filters.quackUserId)
          params.append("quackUserId", filters.quackUserId);
        if (filters.feedbackType?.length) {
          filters.feedbackType.forEach((type) =>
            params.append("feedbackType[]", type)
          );
        }

        if (filters.startDate) params.append("startDate", filters.startDate);
        if (filters.endDate) params.append("endDate", filters.endDate);

        return apiResolver<ReleaseConversationResponse>(
          api.get(`/platform/release-conversation?${params.toString()}`)
        );
      },
      searchReleaseConversations: async (
        filters: SearchReleaseConversationsDto = {}
      ): Promise<SearchGoodFeedbackReleaseConversationsResponse> => {
        const params = new URLSearchParams();

        if (filters.search) params.append("search", filters.search);
        if (filters.semanticSearch)
          params.append("semanticSearch", filters.semanticSearch);
        if (filters.simulator !== undefined)
          params.append("simulator", filters.simulator.toString());
        if (filters.release !== undefined)
          params.append("release", filters.release.toString());
        if (filters.page) params.append("page", filters.page.toString());
        if (filters.limit) params.append("limit", filters.limit.toString());

        return apiResolver<SearchGoodFeedbackReleaseConversationsResponse>(
          api.get(`/platform/release-conversation/search?${params.toString()}`)
        );
      },
      updateReleaseConversationFeedback: async (
        id: number,
        data: UpdateReleaseConversationFeedbackDto
      ): Promise<void> => {
        return apiResolver<void>(
          api.put(`/platform/release-conversation/${id}/feedback`, data)
        );
      },
      batchUpdateReleaseConversationFeedback: async (
        data: BatchUpdateReleaseConversationStatusDto
      ): Promise<BatchUpdateFeedbackResponse> => {
        return apiResolver<BatchUpdateFeedbackResponse>(
          api.put(`/platform/release-conversation/feedbacks`, data)
        );
      },
      getReleaseConversationComments: async (
        id: number
      ): Promise<ReleaseComment[]> => {
        return apiResolver<ReleaseComment[]>(
          api.get(`/platform/release-conversation/conversations/${id}/comments`)
        );
      },
    }),
    [api]
  );
}

export function useReleaseConversations(filters?: ReleaseConversationFilters) {
  const { getReleaseConversations } = useReleaseConversationApi();

  return useQuery({
    queryKey: ["release-conversations", filters],
    queryFn: () => getReleaseConversations(filters),
    enabled: true,
  });
}

export function useSearchReleaseConversations(
  searchDto: SearchReleaseConversationsDto = {},
  enabled: boolean = true
) {
  const { searchReleaseConversations } = useReleaseConversationApi();

  return useQuery({
    queryKey: ["release-conversations-search", searchDto],
    queryFn: () => searchReleaseConversations(searchDto),
    enabled: enabled && (!!searchDto.search || !!searchDto.semanticSearch),
  });
}

export function useUpdateReleaseConversationFeedback() {
  const { updateReleaseConversationFeedback } = useReleaseConversationApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: UpdateReleaseConversationFeedbackDto;
    }) => updateReleaseConversationFeedback(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["release-conversations"]);
      queryClient.invalidateQueries(["release-conversations-search"]);
    },
  });
}

export function useBatchUpdateReleaseConversationFeedback() {
  const { batchUpdateReleaseConversationFeedback } =
    useReleaseConversationApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BatchUpdateReleaseConversationStatusDto) =>
      batchUpdateReleaseConversationFeedback(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["release-conversations"]);
      queryClient.invalidateQueries(["release-conversations-search"]);
    },
  });
}
